// src/components/ui/DeliveryCalendar.jsx
// Calendario visual de fecha de entrega.
// - Solo lectura cuando no se pasa onSelect (vista solicitante).
// - Interactivo cuando se pasa onSelect (sidebar de staff).
import React, { useState } from 'react';

const DAYS   = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
                'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

// Convierte Date local a YYYY-MM-DD sin desfase de zona horaria
function toISODate(year, month, day) {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export default function DeliveryCalendar({ deliveryDate, onSelect }) {
    const interactive = typeof onSelect === 'function';

    // Fecha seleccionada (puede ser null si aún no hay ninguna)
    const delivery = deliveryDate ? new Date(deliveryDate) : null;

    const [viewDate, setViewDate] = useState(() => {
        if (delivery) {
            return new Date(delivery.getUTCFullYear(), delivery.getUTCMonth(), 1);
        }
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });
    const [hovered, setHovered] = useState(null);

    const year        = viewDate.getFullYear();
    const month       = viewDate.getMonth();
    const firstDow    = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const isSelected = (d) =>
        delivery &&
        delivery.getUTCFullYear() === year &&
        delivery.getUTCMonth()    === month &&
        delivery.getUTCDate()     === d;

    const today = new Date();
    const isToday = (d) =>
        today.getFullYear() === year &&
        today.getMonth()    === month &&
        today.getDate()     === d;

    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    const handleDayClick = (day) => {
        if (!interactive) return;
        onSelect(toISODate(year, month, day));
    };

    return (
        <div style={{ fontFamily: 'inherit', userSelect: 'none' }}>

            {/* Navegación mes */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <button
                    onClick={() => setViewDate(new Date(year, month - 1, 1))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer',
                             color: 'var(--c-text-3)', fontSize: 18, padding: '2px 6px', lineHeight: 1 }}
                    aria-label="Mes anterior"
                >‹</button>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-1)' }}>
                    {MONTHS[month]} {year}
                </span>
                <button
                    onClick={() => setViewDate(new Date(year, month + 1, 1))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer',
                             color: 'var(--c-text-3)', fontSize: 18, padding: '2px 6px', lineHeight: 1 }}
                    aria-label="Mes siguiente"
                >›</button>
            </div>

            {/* Encabezado días */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, marginBottom: 4 }}>
                {DAYS.map(d => (
                    <div key={d} style={{
                        textAlign: 'center', fontSize: 9, fontWeight: 700,
                        color: 'var(--c-text-4)', padding: '2px 0',
                    }}>
                        {d}
                    </div>
                ))}
            </div>

            {/* Celdas de días */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                {cells.map((day, i) => {
                    if (day === null) return <div key={`_${i}`} />;

                    const selected  = isSelected(day);
                    const hoy       = !selected && isToday(day);
                    const isHovered = interactive && hovered === day && !selected;

                    let bg    = 'transparent';
                    let color = 'var(--c-text-2)';
                    let fw    = 400;

                    if (selected) {
                        bg    = '#2563eb';
                        color = '#fff';
                        fw    = 700;
                    } else if (isHovered) {
                        bg    = 'var(--c-border, #e2e8f0)';
                        color = 'var(--c-text-1)';
                    } else if (hoy) {
                        bg    = 'var(--c-status-open-bg, #eff6ff)';
                        color = '#2563eb';
                        fw    = 600;
                    }

                    return (
                        <div
                            key={day}
                            onClick={() => handleDayClick(day)}
                            onMouseEnter={() => interactive && setHovered(day)}
                            onMouseLeave={() => interactive && setHovered(null)}
                            style={{
                                textAlign: 'center',
                                fontSize: 12,
                                padding: '6px 2px',
                                borderRadius: 6,
                                fontWeight: fw,
                                background: bg,
                                color,
                                cursor: interactive ? 'pointer' : 'default',
                                transition: 'background 0.1s',
                            }}
                        >
                            {day}
                        </div>
                    );
                })}
            </div>

            {/* Leyenda — solo si hay fecha seleccionada */}
            {delivery && (
                <div style={{
                    marginTop: 12, paddingTop: 10,
                    borderTop: '1px solid var(--c-border)',
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                }}>
                    <div style={{
                        width: 10, height: 10, borderRadius: 3,
                        background: '#2563eb', flexShrink: 0, marginTop: 2,
                    }} />
                    <span style={{ fontSize: 12, color: 'var(--c-text-3)', lineHeight: 1.4 }}>
                        {interactive ? 'Seleccionado:' : 'Fecha de entrega requerida:'}{' '}
                        <strong style={{ color: 'var(--c-text-1)' }}>
                            {delivery.toLocaleDateString('es-MX', {
                                weekday: 'long', day: 'numeric',
                                month: 'long', year: 'numeric',
                                timeZone: 'UTC',
                            })}
                        </strong>
                    </span>
                </div>
            )}

            {/* Hint cuando no hay fecha y es interactivo */}
            {!delivery && interactive && (
                <p style={{ marginTop: 10, fontSize: 11, color: 'var(--c-text-4)', textAlign: 'center' }}>
                    Toca un día para seleccionar la fecha de entrega
                </p>
            )}
        </div>
    );
}
